import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { categories, melons } from "@/mocks/gossip";
import MelonGrid from "./components/MelonGrid";
import PlazaAside from "./components/PlazaAside";

const SITE_URL = "https://pjdflq.cn";
const PAGE_SIZE = 6;

export default function Plaza() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryId = searchParams.get("category") ?? "all";
  const keyword = (searchParams.get("q") ?? "").trim();
  const [localKeyword, setLocalKeyword] = useState(keyword);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const activeCategory = categories.find((item) => item.id === categoryId);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [categoryId, keyword]);

  useEffect(() => {
    setLocalKeyword(keyword);
  }, [keyword]);

  const updateParams = (next: { category?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.category !== undefined) {
      if (next.category === "all") {
        params.delete("category");
      } else {
        params.set("category", next.category);
      }
    }
    if (next.q !== undefined) {
      if (next.q) {
        params.set("q", next.q);
      } else {
        params.delete("q");
      }
    }
    setSearchParams(params);
  };

  const filtered = useMemo(() => {
    return melons.filter((melon) => {
      const matchCategory = categoryId === "all" || melon.categoryId === categoryId;
      if (!matchCategory) return false;
      if (!keyword) return true;
      return (
        melon.title.includes(keyword) ||
        melon.summary.includes(keyword) ||
        melon.categoryName.includes(keyword) ||
        melon.tags.some((tag) => tag.includes(keyword))
      );
    });
  }, [categoryId, keyword]);

  const pageTitle = activeCategory ? `${activeCategory.name} · 瓜田广场` : "瓜田广场";

  useDocumentMeta({
    title: `${pageTitle} - 瓜田吃瓜${keyword ? `（搜索：${keyword}）` : ""}`,
    description: activeCategory
      ? `瓜田吃瓜${activeCategory.name}板块：${activeCategory.desc}。共 ${activeCategory.count} 条内容，按时间线持续补充后续进展。`
      : "瓜田广场汇总全部吃瓜内容，支持按娱乐八卦、影视综艺、社会热点等主题筛选与关键词搜索，每条都附完整时间线。",
    keywords: "瓜田广场,吃瓜内容,娱乐八卦,影视综艺,社会热点,蹲后续",
    path: "/plaza",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: pageTitle,
        url: `${SITE_URL}/plaza`,
        inLanguage: "zh-CN",
        description: "瓜田吃瓜内容集合页，按主题分类整理公开信息与后续进展。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "瓜田广场", item: `${SITE_URL}/plaza` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "瓜田广场内容列表",
        itemListElement: filtered.slice(0, 8).map((melon, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: melon.title,
          url: `${SITE_URL}/article/${melon.id}`,
        })),
      },
    ],
  });

  return (
    <>
      <SiteHeader />
      <main className="w-full">
        <div className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-8">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "瓜田广场" }]} />
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              {activeCategory ? `${activeCategory.name}板块` : "瓜田广场"}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {activeCategory
                ? activeCategory.desc
                : "这里汇总全站内容。支持按主题切换、关键词搜索，每一条都会附上我们整理的时间线，方便你从头蹲到尾。"}
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-12">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-100 p-1">
              <button
                type="button"
                onClick={() => updateParams({ category: "all" })}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  categoryId === "all"
                    ? "bg-primary-600 text-background-50"
                    : "text-foreground-700 hover:bg-background-200"
                }`}
              >
                全部
              </button>
              {categories.slice(0, 5).map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => updateParams({ category: category.id })}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    categoryId === category.id
                      ? "bg-primary-600 text-background-50"
                      : "text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                updateParams({ q: localKeyword.trim() });
              }}
              role="search"
              className="flex items-center gap-2"
            >
              <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2 transition-colors focus-within:border-primary-400 lg:w-72">
                <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-base" aria-hidden="true"></i>
                </span>
                <input
                  type="search"
                  value={localKeyword}
                  onChange={(e) => setLocalKeyword(e.target.value)}
                  placeholder="搜索标题、标签或板块"
                  aria-label="在瓜田广场中搜索"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-primary-600 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                搜索
              </button>
            </form>
          </div>

          {keyword && (
            <p className="mb-5 text-sm text-foreground-600">
              关于「<span className="font-medium text-foreground-900">{keyword}</span>」共找到{" "}
              <span className="font-medium text-primary-700">{filtered.length}</span> 条内容。
              <button
                type="button"
                onClick={() => updateParams({ q: "" })}
                className="ml-3 inline-flex items-center gap-1 text-primary-700 hover:text-primary-800"
              >
                清除搜索
                <i className="ri-close-line text-sm" aria-hidden="true"></i>
              </button>
            </p>
          )}

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <MelonGrid
                melons={filtered}
                visibleCount={visibleCount}
                onLoadMore={() => setVisibleCount((count) => count + PAGE_SIZE)}
              />
            </div>
            <div className="lg:col-span-4">
              <PlazaAside />
            </div>
          </div>
        </div>

        <div className="w-full border-t border-background-200 bg-background-100">
          <div className="mx-auto flex max-w-7xl flex-col items-start gap-4 px-4 py-10 md:flex-row md:items-center md:justify-between md:px-8">
            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                没找到想蹲的那条瓜？
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                留下邮箱订阅蹲后续，新内容整理好之后会优先推送给你。
              </p>
            </div>
            <Link
              to="/#follow"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              去订阅
              <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}